import {
	Address,
	AuthorizedUser,
	BusinessContact,
	createResource,
	FullName,
	getFile,
	getResource,
	OkDocument,
	Permissible,
	Phone,
	RiskRate,
	updateResource,
} from "./common"
import moment from "moment"
import {IcomoonIconName} from "../components/Icon/icons"
import {isUndefined, uniq} from "lodash"
import {
	AnnualIncome,
	BusinessAccountPurpose,
	BusinessAnnualRevenue,
	BusinessIndustry,
	BusinessNumberOfEmployees,
	BusinessSourceOfFunds,
	BusinessTransactionVolume,
	BusinessVertical,
	CashFlow,
	IndividualAccountPurpose,
	IndividualSourceOfFunds,
	IndividualTransactionVolume,
	Occupation,
	Profession,
	SoleProprietorshipAnnualRevenue,
	SoleProprietorshipNumberOfEmployees,
	SolePropTransactionVolume,
	SourceOfIncome,
} from "./application"

export type BusinessEntityType =
	| "Corporation"
	| "LLC"
	| "Partnership"
	| "PubliclyTradedCorporation"
	| "PrivatelyHeldCorporation"
	| "NotForProfitOrganization"
	| "Charity"
	| "ReligiousOrganization"
	| "GovernmentEntityOrAgency"
	| "DomesticFinancialInstitution"
	| "ForeignFinancialInstitution"
	| "Trust"
	| "Estate"

export const businessEntityTypeLabels: Map<BusinessEntityType, string> = new Map([
	["Corporation", "Corporation"],
	["LLC", "LLC"],
	["Partnership", "Partnership"],
	["PubliclyTradedCorporation", "Publicly Traded Corporation"],
	["PrivatelyHeldCorporation", "Privately Held Corporation"],
	["NotForProfitOrganization", "Not For Profit Organization"],
	["Charity", "Charity"],
	["ReligiousOrganization", "Religious Organization"],
	["GovernmentEntityOrAgency", "Government Entity Or Agency"],
	["DomesticFinancialInstitution", "Domestic Financial Institution"],
	["ForeignFinancialInstitution", "Foreign Financial Institution"],
	["Trust", "Trust"],
	["Estate", "Estate"],
])

export type BusinessCustomer = {
	type: "businessCustomer"
	id: string
	attributes: {
		createdAt: Date
		name: string
		dba?: string
		address: Permissible<Address>
		phone: Permissible<Phone>
		dateOfIncorporation: Date
		stateOfIncorporation: string
		ein: Permissible<string>
		entityType?: BusinessEntityType
		contact: BusinessContact
		purpose: string
		tags: {[key: string]: string}
		riskRate?: RiskRate
		overrideRiskRate?: RiskRate
		authorizedUsers: AuthorizedUser[]
		status: CustomerStatus
		archiveReason?: ArchiveReason
		website?: string
		countriesOfOperation?: string[]
		externalCip?: boolean
		operatingAddress?: Permissible<Address>
		stockSymbol?: string
		businessVertical?: BusinessVertical
		numberOfEmployees?: BusinessNumberOfEmployees
		annualRevenue?: BusinessAnnualRevenue
		cashFlow?: CashFlow
		yearOfIncorporation?: string
		attestedNoBeneficialOwners?: boolean
		businessIndustry?: BusinessIndustry
		sourceOfFunds?: BusinessSourceOfFunds
		sourceOfFundsDescription?: string
		accountPurpose?: BusinessAccountPurpose
		accountPurposeDetail?: string
		transactionVolume?: BusinessTransactionVolume
		transactionVolumeDescription?: string
		isRegulated?: boolean
		regulatorName?: string
		usNexus?: string[]
		businessDescription?: string
	}
	relationships: {
		org: {
			data: {
				type: "org"
				id: string
			}
		}
		application?: {
			data: {
				type: "businessApplication"
				id: string
			}
		}
	}
}

export type IndividualCustomer = {
	type: "individualCustomer"
	id: string
	attributes: {
		createdAt: Date
		fullName: FullName
		ssn: Permissible<string>
		passport: Permissible<string>
		nationality: Permissible<string>
		matriculaConsular: Permissible<string>
		address: Permissible<Address>
		dateOfBirth: Permissible<Date>
		email: Permissible<string>
		phone: Permissible<Phone>
		tin?: string
		ip?: string
		ein: Permissible<string>
		dba?: string
		soleProprietorship: boolean
		tags: {[key: string]: string}
		riskRate?: RiskRate
		overrideRiskRate?: RiskRate
		maskedSSN?: string
		maskedPassport?: string
		maskedMatriculaConsular?: string
		authorizedUsers: AuthorizedUser[]
		status: CustomerStatus
		archiveReason?: ArchiveReason
		occupation?: Occupation
		sourceOfIncome?: SourceOfIncome
		annualIncome?: AnnualIncome
		businessVertical?: BusinessVertical
		numberOfEmployees?: SoleProprietorshipNumberOfEmployees
		annualRevenue?: SoleProprietorshipAnnualRevenue
		website?: string
		sourceOfFunds?: IndividualSourceOfFunds | BusinessSourceOfFunds
		sourceOfFundsDescription?: string
		accountPurpose?: IndividualAccountPurpose | BusinessAccountPurpose
		accountPurposeDetail?: string
		transactionVolume?: IndividualTransactionVolume | SolePropTransactionVolume
		transactionVolumeDescription?: string
		profession?: Profession
		businessIndustry?: BusinessIndustry
		usNexus?: string[]
		isIncorporated?: boolean
		stateOfIncorporation?: string
		yearOfIncorporation?: string
		countriesOfOperation?: string[]
		businessDescription?: string
	}
	relationships: {
		org: {
			data: {
				type: "org"
				id: string
			}
		}
		application?: {
			data: {
				type: "individualApplication"
				id: string
			}
		}
	}
}

export type BusinessFBOCustomer = {
	type: "businessFBOCustomer"
	id: string
	attributes: {
		createdAt: Date
		businessName: string
		businessVertical: string
		numberOfEmployees: string
		bankName: string
		name: string
		address: Permissible<Address>
		tags: {[key: string]: string}
		status: CustomerStatus
		authorizedUsers: AuthorizedUser[]
	}
	relationships: {
		org: {
			data: {
				type: "org"
				id: string
			}
		}
		// Not in use
		application?: {
			data: {
				type: "individualApplication"
				id: string
			}
		}
	}
}

export type IndividualWalletCustomer = {
	type: "individualWalletCustomer"
	id: string
	attributes: {
		createdAt: Date
		fullName: FullName
		email: Permissible<string>
		phone: Phone
		status: CustomerStatus
		tags: {[key: string]: string}
		authorizedUsers: AuthorizedUser[]
	}
	relationships: {
		org: {
			data: {
				type: "org"
				id: string
			}
		}
		// Not in use
		application?: {
			data: {
				type: "individualWalletApplication"
				id: string
			}
		}
	}
}

export type BusinessWalletCustomer = {
	type: "businessWalletCustomer"
	id: string
	attributes: {
		createdAt: Date
		businessName: string
		ein: string
		businessVertical: string
		numberOfEmployees: string
		address: Address
		tags: {[key: string]: string}
		status: CustomerStatus
		authorizedUsers: AuthorizedUser[]
	}
	relationships: {
		org: {
			data: {
				type: "org"
				id: string
			}
		}
		// Not in use
		application?: {
			data: {
				type: "businessWalletApplication"
				id: string
			}
		}
	}
}

export type Customer =
	| IndividualCustomer
	| BusinessCustomer
	| BusinessFBOCustomer
	| IndividualWalletCustomer
	| BusinessWalletCustomer

export enum CustomerType {
	individual = "individual",
	business = "business",
	businessFBO = "businessFBO",
	individualWallet = "individualWallet",
	businessWallet = "businessWallet",
}

export const CustomerResourceTypes = [
	"individualCustomer",
	"businessCustomer",
	"businessFBOCustomer",
	"individualWalletCustomer",
	"businessWalletCustomer",
]
export enum CustomerStatus {
	Active = "Active",
	Archived = "Archived",
}

export type CustomerInclude = "org" | "officer" | "beneficialOwner"

export enum ArchiveReason {
	Inactive = "Inactive",
	FraudACHActivity = "FraudACHActivity",
	FraudCardActivity = "FraudCardActivity",
	FraudCheckActivity = "FraudCheckActivity",
	FraudApplicationHistory = "FraudApplicationHistory",
	FraudAccountActivity = "FraudAccountActivity",
	FraudClientIdentified = "FraudClientIdentified",
	FraudLinkedToFraudulentCustomer = "FraudLinkedToFraudulentCustomer",
	ByBank = "ByBank",
}

export const CustomerTypeIcons: Record<CustomerType, IcomoonIconName> = {
	[CustomerType.business]: "toolbox",
	[CustomerType.individual]: "user-geometric-action---users",
	[CustomerType.businessFBO]: "toolbox",
	[CustomerType.individualWallet]: "user-geometric-action---users",
	[CustomerType.businessWallet]: "toolbox",
}

export interface CustomerStatusChange {
	status: string
	reason?: string
}

export async function findCustomers(
	accessToken: string,
	offset: number,
	limit: number,
	sort: string,
	searchQuery: string,
	orgs?: string[],
	filterByStatus?: CustomerStatus[],
	filterByType?: CustomerType[],
	banks?: string[],
	resourcePath = "customers"
) {
	const orgFilter = orgs && orgs.length > 0 ? orgs : null
	const bankFilter = banks && banks.length > 0 ? banks : null

	const query = {
		page: {
			limit,
			offset,
		},
		include: "org",
		filter: {
			query: searchQuery != "" ? searchQuery : null,
			orgs: orgFilter,
			status: filterByStatus,
			types: filterByType,
			banks: bankFilter,
		},
		sort,
	}

	return await getResource<Array<Customer>>(resourcePath, accessToken, query)
}

export async function exportCustomers(
	accessToken: string,
	sort: string,
	searchQuery: string,
	orgs?: string[],
	types?: CustomerType[],
	banks?: string[],
	resourcePath = "customers"
) {
	const orgFilter = orgs && orgs.length > 0 ? orgs : null
	const typeFilter = types && types.length > 0 ? types : null
	const bankFilter = banks && banks.length > 0 ? banks : null

	const query = {
		page: {
			limit: 10000,
			offset: 0,
		},
		filter: {
			query: searchQuery != "" ? searchQuery : null,
			orgs: orgFilter,
			types: typeFilter,
			banks: bankFilter,
		},
		include: "org",
		sort,
	}

	return await getFile(resourcePath, accessToken, query)
}

export async function getCustomer(accessToken: string, id: string, include: CustomerInclude[] = ["org"]) {
	const query = {
		include: uniq(include).join(","),
		extraFields: {
			customer: "maskedSSN,maskedPassport,maskedMatriculaConsular",
		},
	}
	return await getResource<Customer>(`customers/${id}`, accessToken, query)
}

export async function getBusiness<Business>(accessToken: string, id: string) {
	const query = {
		include: "org",
	}
	return await getResource<Business>(`customers/${id}`, accessToken, query)
}

export type UpdateIndividualCustomerAttributes = {
	ssn?: string
	passport?: string
	nationality?: string
	matriculaConsular?: string
	dob?: Date
	address?: Address
	phone?: Phone
	email?: string
	ein?: string
	dba?: string | null
	riskRate?: RiskRate
	fullName?: FullName
	occupation?: Occupation
	sourceOfIncome?: SourceOfIncome
	annualIncome?: AnnualIncome
	businessVertical?: BusinessVertical
	annualRevenue?: SoleProprietorshipAnnualRevenue
	numberOfEmployees?: SoleProprietorshipNumberOfEmployees
	website?: string
	sourceOfFunds?: IndividualSourceOfFunds | BusinessSourceOfFunds
	sourceOfFundsDescription?: string
	accountPurpose?: IndividualAccountPurpose | BusinessAccountPurpose
	accountPurposeDetail?: string
	transactionVolume?: IndividualTransactionVolume | SolePropTransactionVolume
	transactionVolumeDescription?: string
	profession?: Profession
	businessIndustry?: BusinessIndustry
	usNexus?: string[]
	isIncorporated?: boolean
	stateOfIncorporation?: string
	yearOfIncorporation?: string
	countriesOfOperation?: string[]
	businessDescription?: string
}

export async function updateIndividualCustomer(
	accessToken: string,
	Customer: IndividualCustomer,
	{
		ssn,
		passport,
		matriculaConsular,
		dob,
		address,
		phone,
		nationality,
		email,
		ein,
		dba,
		riskRate,
		fullName,
		occupation,
		sourceOfIncome,
		annualIncome,
		businessVertical,
		annualRevenue,
		numberOfEmployees,
		website,
		sourceOfFunds,
		sourceOfFundsDescription,
		accountPurpose,
		accountPurposeDetail,
		transactionVolume,
		transactionVolumeDescription,
		profession,
		businessIndustry,
		usNexus,
		isIncorporated,
		stateOfIncorporation,
		yearOfIncorporation,
		countriesOfOperation,
		businessDescription,
	}: UpdateIndividualCustomerAttributes
) {
	return await updateResource<Customer>(`customers/${Customer.id}`, accessToken, {
		type: Customer.type,
		attributes: Object.assign(
			{},
			ssn ? {ssn: ssn} : undefined,
			passport ? {passport: passport} : undefined,
			nationality ? {nationality: nationality} : undefined,
			matriculaConsular ? {matriculaConsular: matriculaConsular} : undefined,
			ein ? {ein: ein} : undefined,
			dob ? {dateOfBirth: moment(dob).format("YYYY-MM-DD")} : undefined,
			address ? {address: address} : undefined,
			phone ? {phone: phone} : undefined,
			email ? {email: email} : undefined,
			!isUndefined(dba) ? {dba: dba} : undefined,
			riskRate ? {riskRate: riskRate} : undefined,
			fullName ? {fullName: fullName} : undefined,
			occupation ? {occupation: occupation} : undefined,
			sourceOfIncome ? {sourceOfIncome: sourceOfIncome} : undefined,
			annualIncome ? {annualIncome: annualIncome} : undefined,
			businessVertical ? {businessVertical: businessVertical} : undefined,
			annualRevenue ? {annualRevenue: annualRevenue} : undefined,
			numberOfEmployees ? {numberOfEmployees: numberOfEmployees} : undefined,
			website ? {website: website} : undefined,
			sourceOfFunds ? {sourceOfFunds: sourceOfFunds} : undefined,
			sourceOfFundsDescription ? {sourceOfFundsDescription: sourceOfFundsDescription} : undefined,
			accountPurpose ? {accountPurpose: accountPurpose} : undefined,
			accountPurposeDetail ? {accountPurposeDetail: accountPurposeDetail} : undefined,
			transactionVolume ? {transactionVolume: transactionVolume} : undefined,
			transactionVolumeDescription ? {transactionVolumeDescription: transactionVolumeDescription} : undefined,
			profession ? {profession: profession} : undefined,
			businessIndustry ? {businessIndustry: businessIndustry} : undefined,
			!isUndefined(usNexus) ? {usNexus: usNexus} : undefined,
			!isUndefined(isIncorporated) ? {isIncorporated: isIncorporated} : undefined,
			stateOfIncorporation ? {stateOfIncorporation: stateOfIncorporation} : undefined,
			yearOfIncorporation ? {yearOfIncorporation: yearOfIncorporation} : undefined,
			!isUndefined(countriesOfOperation) ? {countriesOfOperation: countriesOfOperation} : undefined,
			businessDescription ? {businessDescription: businessDescription} : undefined
		),
	})
}

export type UpdateBusinessCustomerAttributes = {
	contactName?: FullName
	contactPhone?: Phone
	contactEmail?: string
	address?: Address
	phone?: Phone
	ein?: string
	dba?: string | null
	stateOfIncorporation?: string
	riskRate?: RiskRate
	name?: string
	operatingAddress?: Address
	entityType?: BusinessEntityType
	stockSymbol?: string
	businessVertical?: BusinessVertical
	website?: string
	numberOfEmployees?: BusinessNumberOfEmployees
	yearOfIncorporation?: string
	annualRevenue?: BusinessAnnualRevenue
	cashFlow?: CashFlow
	countriesOfOperation?: string[]
	businessIndustry?: BusinessIndustry
	sourceOfFunds?: BusinessSourceOfFunds
	sourceOfFundsDescription?: string
	accountPurpose?: BusinessAccountPurpose
	accountPurposeDetail?: string
	transactionVolume?: BusinessTransactionVolume
	transactionVolumeDescription?: string
	isRegulated?: boolean
	regulatorName?: string
	usNexus?: string[]
	businessDescription?: string
}

export async function updateBusinessCustomer(
	accessToken: string,
	customer: BusinessCustomer,
	{
		contactPhone,
		contactName,
		contactEmail,
		address,
		phone,
		ein,
		dba,
		stateOfIncorporation,
		riskRate,
		name,
		operatingAddress,
		entityType,
		stockSymbol,
		businessVertical,
		website,
		numberOfEmployees,
		yearOfIncorporation,
		annualRevenue,
		cashFlow,
		countriesOfOperation,
		businessIndustry,
		sourceOfFunds,
		sourceOfFundsDescription,
		accountPurpose,
		accountPurposeDetail,
		transactionVolume,
		transactionVolumeDescription,
		isRegulated,
		regulatorName,
		usNexus,
		businessDescription,
	}: UpdateBusinessCustomerAttributes
) {
	return await updateResource<Customer>(`customers/${customer.id}`, accessToken, {
		type: customer.type,
		attributes: Object.assign(
			{},
			address ? {address: address} : undefined,
			phone ? {phone: phone} : undefined,
			ein ? {ein: ein} : undefined,
			!isUndefined(dba) ? {dba: dba} : undefined,
			riskRate ? {riskRate: riskRate} : undefined,
			stateOfIncorporation ? {stateOfIncorporation: stateOfIncorporation} : undefined,
			contactName || contactPhone || contactEmail
				? {
						contact: Object.assign(
							{},
							contactName ? {fullName: contactName} : undefined,
							contactPhone ? {phone: contactPhone} : undefined,
							contactEmail ? {email: contactEmail} : undefined
						),
				  }
				: undefined,
			name ? {name: name} : undefined,
			operatingAddress ? {operatingAddress: operatingAddress} : undefined,
			entityType ? {entityType: entityType} : undefined,
			stockSymbol ? {stockSymbol: stockSymbol} : undefined,
			businessVertical ? {businessVertical: businessVertical} : undefined,
			website ? {website: website} : undefined,
			numberOfEmployees ? {numberOfEmployees: numberOfEmployees} : undefined,
			yearOfIncorporation ? {yearOfIncorporation: yearOfIncorporation} : undefined,
			annualRevenue ? {annualRevenue: annualRevenue} : undefined,
			cashFlow ? {cashFlow: cashFlow} : undefined,
			!isUndefined(countriesOfOperation) ? {countriesOfOperation: countriesOfOperation} : undefined,
			businessIndustry ? {businessIndustry: businessIndustry} : undefined,
			sourceOfFunds ? {sourceOfFunds: sourceOfFunds} : undefined,
			sourceOfFundsDescription ? {sourceOfFundsDescription: sourceOfFundsDescription} : undefined,
			accountPurpose ? {accountPurpose: accountPurpose} : undefined,
			accountPurposeDetail ? {accountPurposeDetail: accountPurposeDetail} : undefined,
			transactionVolume ? {transactionVolume: transactionVolume} : undefined,
			transactionVolumeDescription ? {transactionVolumeDescription: transactionVolumeDescription} : undefined,
			!isUndefined(isRegulated) ? {isRegulated: isRegulated} : undefined,
			regulatorName ? {regulatorName: regulatorName} : undefined,
			!isUndefined(usNexus) ? {usNexus: usNexus} : undefined,
			businessDescription ? {businessDescription: businessDescription} : undefined
		),
	})
}

export async function updateBusinessWalletCustomer(
	accessToken: string,
	customer: BusinessWalletCustomer,
	{
		businessName,
		address,
		ein,
	}: {
		businessName?: string
		address?: Address
		ein?: string
	}
) {
	return await updateResource<Customer>(`customers/${customer.id}`, accessToken, {
		type: customer.type,
		attributes: Object.assign(
			{},
			businessName ? {businessName: businessName} : undefined,
			address ? {address: address} : undefined,
			ein ? {ein: ein} : undefined
		),
	})
}

export async function updateBusinessFBOCustomer(
	accessToken: string,
	customer: BusinessFBOCustomer,
	{
		businessVertical,
		numberOfEmployees,
		bankName,
		address,
		businessName,
	}: {
		businessVertical?: string
		numberOfEmployees?: string
		bankName?: string
		address?: Address
		businessName?: string
	}
) {
	return await updateResource<Customer>(`customers/${customer.id}`, accessToken, {
		type: customer.type,
		attributes: Object.assign(
			{},
			businessVertical ? {businessVertical: businessVertical} : undefined,
			numberOfEmployees ? {numberOfEmployees: numberOfEmployees} : undefined,
			bankName ? {bankName: bankName} : undefined,
			address ? {address: address} : undefined,
			businessName ? {businessName: businessName} : undefined
		),
	})
}

export async function updateCustomerAuthorizedUsers(
	accessToken: string,
	customer: OkDocument<Customer>,
	authorizedUsers: AuthorizedUser[]
) {
	return await updateResource<Customer>(`customers/${customer.data.id}`, accessToken, {
		type: customer.data.type,
		attributes: {
			authorizedUsers: authorizedUsers,
		},
	})
}

export async function updateCustomerTags(
	accessToken: string,
	customerId: string,
	customerType: Customer["type"],
	tags: {[key: string]: string | null}
) {
	const result = await updateResource<Customer>(`customers/${customerId}`, accessToken, {
		type: customerType,
		attributes: {
			tags,
		},
	})
	return result.map((v) => v.data)
}

export async function archiveCustomer(accessToken: string, customerId: string, reason: ArchiveReason) {
	const data = {
		type: "archiveCustomer",
		attributes: {
			reason: reason,
		},
	}
	return await createResource<Customer>(`customers/${customerId}/archive`, accessToken, data)
}

export async function unarchiveCustomer(accessToken: string, customerId: string) {
	return await createResource<Customer>(`customers/${customerId}/unarchive`, accessToken, {})
}

export function isBusinessDocument(customer: OkDocument<Customer>): customer is OkDocument<BusinessCustomer> {
	return customer.data.type === "businessCustomer"
}

export function isIndividualDocument(customer: OkDocument<Customer>): customer is OkDocument<IndividualCustomer> {
	return customer.data.type === "individualCustomer"
}

export function isBusinessFBODocument(customer: OkDocument<Customer>): customer is OkDocument<BusinessFBOCustomer> {
	return customer.data.type === "businessFBOCustomer"
}

export function isIndividualWalletDocument(
	customer: OkDocument<Customer>
): customer is OkDocument<IndividualWalletCustomer> {
	return customer.data.type === "individualWalletCustomer"
}

export function isBusinessWalletDocument(
	customer: OkDocument<Customer>
): customer is OkDocument<BusinessWalletCustomer> {
	return customer.data.type === "businessWalletCustomer"
}

export function isBusinessCustomer(customer: Customer): customer is BusinessCustomer {
	return customer.type === "businessCustomer"
}

export function isIndividualCustomer(customer: Customer): customer is IndividualCustomer {
	return customer.type === "individualCustomer"
}

export function isBusinessFBOCustomer(customer: Customer): customer is BusinessFBOCustomer {
	return customer.type === "businessFBOCustomer"
}

export function isIndividualWalletCustomer(customer: Customer): customer is IndividualWalletCustomer {
	return customer.type === "individualWalletCustomer"
}

export function isBusinessWalletCustomer(customer: Customer): customer is BusinessWalletCustomer {
	return customer.type === "businessWalletCustomer"
}

export function getCustomerName(customer: Customer): string {
	if (isIndividualCustomer(customer) || isIndividualWalletCustomer(customer)) {
		return `${customer.attributes.fullName.first} ${customer.attributes.fullName.last}`
	}

	if (isBusinessWalletCustomer(customer)) {
		return customer.attributes.businessName || ""
	}

	return customer.attributes.name || ""
}
